import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Playlist, Track } from '../types/music';
import { readStore, writeStore } from '../utils/storage';
interface LibraryValue {
  favorites: Track[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (track: Track) => void;
  playlists: Playlist[];
  createPlaylist: (name: string, tracks?: Track[], note?: string) => string;
  renamePlaylist: (id: string, name: string) => void;
  updateNote: (id: string, note: string) => void;
  deletePlaylist: (id: string) => void;
  addToPlaylist: (id: string, track: Track) => void;
  removeFromPlaylist: (id: string, trackId: string) => void;
  recentSearches: string[];
  rememberSearch: (term: string) => void;
}
const LibraryContext = createContext<LibraryValue | null>(null);
function makeId(): string {
  return `pl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
export function LibraryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [favorites, setFavorites] = useState<Track[]>(() => readStore<Track[]>('favorites', []));
  const [playlists, setPlaylists] = useState<Playlist[]>(() => readStore<Playlist[]>('playlists', []));
  const [recentSearches, setRecentSearches] = useState<string[]>(() => readStore<string[]>('recentSearches', []));
  useEffect(() => writeStore('favorites', favorites), [favorites]);
  useEffect(() => writeStore('playlists', playlists), [playlists]);
  useEffect(() => writeStore('recentSearches', recentSearches), [recentSearches]);
  const favoriteIds = useMemo(() => new Set(favorites.map(t => t.id)), [favorites]);
  const isFavorite = useCallback((id: string) => favoriteIds.has(id), [favoriteIds]);
  const toggleFavorite = useCallback((track: Track) => {
    setFavorites(current => current.some(t => t.id === track.id) ? current.filter(t => t.id !== track.id) : [track, ...current]);
  }, []);
  const createPlaylist = useCallback((name: string, tracks: Track[] = [], note = '') => {
    const id = makeId();
    const playlist: Playlist = {
      id,
      name: name.trim() || 'Untitled set',
      note,
      createdAt: Date.now(),
      tracks
    };
    setPlaylists(current => [playlist, ...current]);
    return id;
  }, []);
  const renamePlaylist = useCallback((id: string, name: string) => {
    setPlaylists(current => current.map(p => p.id === id ? {
      ...p,
      name: name.trim() || p.name
    } : p));
  }, []);
  const updateNote = useCallback((id: string, note: string) => {
    setPlaylists(current => current.map(p => p.id === id ? {
      ...p,
      note
    } : p));
  }, []);
  const deletePlaylist = useCallback((id: string) => {
    setPlaylists(current => current.filter(p => p.id !== id));
  }, []);
  const addToPlaylist = useCallback((id: string, track: Track) => {
    setPlaylists(current => current.map(p => p.id === id && !p.tracks.some(t => t.id === track.id) ? {
      ...p,
      tracks: [...p.tracks, track]
    } : p));
  }, []);
  const removeFromPlaylist = useCallback((id: string, trackId: string) => {
    setPlaylists(current => current.map(p => p.id === id ? {
      ...p,
      tracks: p.tracks.filter(t => t.id !== trackId)
    } : p));
  }, []);
  const rememberSearch = useCallback((term: string) => {
    const clean = term.trim();
    if (clean.length < 2) return;
    setRecentSearches(current => [clean, ...current.filter(t => t.toLowerCase() !== clean.toLowerCase())].slice(0, 8));
  }, []);
  return <LibraryContext.Provider value={{
    favorites,
    isFavorite,
    toggleFavorite,
    playlists,
    createPlaylist,
    renamePlaylist,
    updateNote,
    deletePlaylist,
    addToPlaylist,
    removeFromPlaylist,
    recentSearches,
    rememberSearch
  }} data-id="element-262" data-mp="jrutx1ghbr52">
      {children}
    </LibraryContext.Provider>;
}
export function useLibrary(): LibraryValue {
  const ctx = useContext(LibraryContext);
  if (!ctx) throw new Error('useLibrary must be used inside a LibraryProvider');
  return ctx;
}